import { isNil } from 'lodash-es'
import { toDiscountDisplay, toTierSummaryText, toTierTooltipText } from '@/services/settlement/settlementPromotion/discountValue'
import { PromotionSettlementLine, PromotionSettlementSummaryRow } from '@/services/settlement/settlementPromotion/types'

/**
 * 파트너정산 프로모션 탭의 고정행 (LOGISPRD-11360 · 시안 02_파트너정산_프로모션할인_v2.html:321)
 * - TOTAL: 전체 합계
 * - SUBTOTAL: 프로모션별 소계
 */
export type RowKind = 'TOTAL' | 'SUBTOTAL'

export interface PinnedRow {
  kind: RowKind
  promotionName?: string
  serviceType?: PromotionSettlementSummaryRow['serviceType']
  discountType?: PromotionSettlementSummaryRow['discountType']
  discountValue?: string
  /** 기간 프로모션의 구간 — 할인값 열이 이 배열로 표기된다 (LOGISPRD-11772) */
  tiers?: PromotionSettlementSummaryRow['tiers']
  settlementCycle?: PromotionSettlementSummaryRow['settlementCycle']
  country?: PromotionSettlementSummaryRow['country']
  quantity: number | null
  originAmount: number
  discountAmount: number
  discountedAmount: number
}

/** 고정행과 라인이 같은 열 정의를 쓴다. 라인에 없는 축(정산 주기·국가)은 고정행에만 값이 있다. */
export type PromotionTabRow = PinnedRow | PromotionSettlementLine

export const isPinnedRow = (row?: PromotionTabRow): row is PinnedRow => !!row && 'kind' in row
export const isTotalRow = (row?: PromotionTabRow) => isPinnedRow(row) && row.kind === 'TOTAL'

/** 합계행은 정산 주기를 비운다 — 여러 프로모션의 주기가 섞여 한 값으로 적을 수 없다 */
export const settlementCycleOf = (row?: PromotionTabRow) => (isTotalRow(row) ? undefined : row?.settlementCycle)
const tiersOf = (row?: PromotionTabRow) => (isPinnedRow(row) ? row.tiers : undefined)

/**
 * 할인값 열 표기 (LOGISPRD-11772).
 *
 * 기간(MONTHLY)은 할인값을 구간에서만 받아 프로모션의 할인값이 0 이다 (BE 등록 규약).
 * 그 0 을 그대로 찍으면 '0%' 로 읽히므로 셀에는 구간 개수만 넣고 구간 전체는 호버로 편다 —
 * 프로모션 관리 목록의 표기와 같다 (LOGISPRD-11390).
 * 구간은 소계 고정행에만 실린다. 라인 조회 응답에는 구간 축이 없어 라인은 '-' 다.
 */
export function promotionDiscountValueText(row?: PromotionTabRow): string {
  if (isTotalRow(row) || !row?.discountType) return ''
  if (settlementCycleOf(row)?.code === 'MONTHLY') return toTierSummaryText(tiersOf(row))
  if (isNil(row.discountValue)) return ''
  // 할인값은 문자열 그대로 쓴다 — Number 로 바꾸면 정액 16자리가 반올림된다 (LOGISPRD-11361)
  return row.discountType.code === 'RATE' ? `${row.discountValue}%` : `${toDiscountDisplay(row.discountValue, false)}원`
}

/**
 * 할인값 열 호버 내용 — 기간 프로모션의 구간 전체 (LOGISPRD-11772).
 * 구간이 없으면 undefined 로 툴팁을 끈다. 줄바꿈은 `GridTooltip` 의 `white-space: pre-line` 이 살린다.
 */
export function promotionTierTooltipText(row?: PromotionTabRow): string | undefined {
  const tiers = tiersOf(row)
  if (settlementCycleOf(row)?.code !== 'MONTHLY' || !tiers?.length) return undefined
  return toTierTooltipText(tiers, row?.discountType?.code === 'FIXED')
}

/**
 * 집계 목록에서 고정행을 만든다 — 전체 합계 1행 + 프로모션별 소계 N행.
 * 수량은 원본에 수량이 없는 서비스(택배비)가 섞이면 null 이 아닌 행만 더한다.
 */
export function buildPinnedRows(summaryRows: PromotionSettlementSummaryRow[]): PinnedRow[] {
  if (!summaryRows.length) return []

  const total = summaryRows.reduce<PinnedRow>(
    (acc, row) => ({
      ...acc,
      quantity: isNil(row.quantity) ? acc.quantity : (acc.quantity ?? 0) + row.quantity,
      originAmount: acc.originAmount + row.originSettlementAmount,
      discountAmount: acc.discountAmount + row.discountAmount,
      discountedAmount: acc.discountedAmount + row.discountedSettlementAmount,
    }),
    { kind: 'TOTAL', quantity: null, originAmount: 0, discountAmount: 0, discountedAmount: 0 },
  )

  const subtotals = summaryRows.map<PinnedRow>((row) => ({
    kind: 'SUBTOTAL',
    promotionName: row.promotionName,
    serviceType: row.serviceType,
    discountType: row.discountType,
    discountValue: row.discountValue,
    tiers: row.tiers,
    settlementCycle: row.settlementCycle,
    country: row.country,
    quantity: row.quantity,
    originAmount: row.originSettlementAmount,
    discountAmount: row.discountAmount,
    discountedAmount: row.discountedSettlementAmount,
  }))

  return [total, ...subtotals]
}

/**
 * 라인 조회의 프로모션 지정 (LOGISPRD-11359).
 *
 * 국가는 서버에서 거른다 — 고른 국가의 프로모션 id 만 넘긴다. 라인 응답에도 국가가 실리지만
 * 클라이언트에서 거르면 페이지마다 걸러지는 수가 달라져 페이지 경계가 어긋난다.
 * 국가 미선택이면 필드를 보내지 않는다 — BE 가 파트너·기간 안 전체를 대상으로 잡아 목록과 범위가 같다.
 * 빈 배열은 BE 가 거부하므로(`@Size(min = 1)`) 대상이 없으면 요청 자체를 하지 않는다.
 *
 * @param filteredSummaryRows 호출부에서 이미 국가로 걸러진 집계 행
 * @param hasCountryFilter 국가가 선택됐는가 — 걸러는 호출부가 하고, 여기서는 이 값만 본다
 */
export function buildLineQueryScope(
  filteredSummaryRows: PromotionSettlementSummaryRow[],
  hasCountryFilter: boolean,
): { promotionIds?: number[]; enabled: boolean } {
  if (!hasCountryFilter) return { promotionIds: undefined, enabled: true }
  if (!filteredSummaryRows.length) return { promotionIds: undefined, enabled: false }
  return { promotionIds: filteredSummaryRows.map((row) => row.promotionId), enabled: true }
}

// 고정행(합계 1행 + 프로모션 소계 N행) 한 줄 높이와, 한 화면에 보일 최대 줄 수.
// 더 있으면 그 안에서 스크롤한다 — 프로모션이 많을 때 본문(품목 라인) 자리를 다 먹지 않게.
// 합계 포함 2줄까지만 노출한다 — 3줄이면 표가 짧을 때 본문이 1~2행으로 짓눌린다 (LOGISPRD-11505).
export const PINNED_ROW_HEIGHT = 40
export const MAX_VISIBLE_PINNED_ROWS = 2

/**
 * 고정행 영역 높이. AG Grid 가 행 수만큼 박는 inline height 를 대신 계산한다.
 * 고정으로 두면 고정행이 MAX_VISIBLE_PINNED_ROWS 줄 미만일 때 남는 자리가 빈 띠로 보인다.
 * 0 줄과 +1px(셀 아래 테두리 몫)은 AG Grid 계산과 같은 조건이다 (GridBodyCtrl.setFloatingHeights).
 */
export function pinnedAreaHeight(rowCount: number): number {
  if (rowCount <= 0) return 0
  return Math.min(rowCount, MAX_VISIBLE_PINNED_ROWS) * PINNED_ROW_HEIGHT + 1
}
